import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  IconBold,
  IconCheck,
  IconItalic,
  IconLinkAlt,
  IconListOl,
  IconListUl,
  IconPen,
  IconTextStrikeThrough,
  IconUnderline,
} from '@maia/icons';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  insertList,
  $isListNode,
} from '@lexical/list';

import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  RangeSelection,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import { RichTextEditorAction } from '../../../atoms/rich-text-editor-action/rich-text-editor-action';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $isAtNodeEnd } from '@lexical/selection';
import { getSelectedNode } from '../richt-text-editor.config';
import { createPortal } from 'react-dom';
import { ActionButton, Input, Typography } from '../../../ui';
import { twMerge } from 'tailwind-merge';

const LowPriority = 1;

function Divider() {
  return <div className="divider" />;
}

function FloatingLinkEditor({ editor }) {
  const editorRef = useRef(null);
  const inputRef = useRef(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [isEditMode, setEditMode] = useState(false);
  const [lastSelection, setLastSelection] = useState(null);

  const updateLinkEditor = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent)) {
        setLinkUrl(parent.getURL());
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL());
      } else {
        setLinkUrl('');
      }
    }
    const editorElem = editorRef.current;
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;

    if (editorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();
    if (
      selection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const domRange = nativeSelection.getRangeAt(0);
      let rect;
      if (nativeSelection.anchorNode === rootElement) {
        let inner = rootElement;
        while (inner.firstElementChild != null) {
          inner = inner.firstElementChild;
        }
        rect = inner.getBoundingClientRect();
      } else {
        rect = domRange.getBoundingClientRect();
      }
      if (!rect) {
        editorRef.current.style.display = 'none';
      }
      setLastSelection(selection);
    } else if (!activeElement || activeElement.className !== 'link-input') {
      setLastSelection(null);
      setEditMode(false);
      setLinkUrl('');
    }

    return true;
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateLinkEditor();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkEditor();
          return true;
        },
        LowPriority,
      ),
    );
  }, [editor, updateLinkEditor]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkEditor();
    });
  }, [editor, updateLinkEditor]);

  useEffect(() => {
    if (isEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditMode]);

  return (
    <div
      ref={editorRef}
      className={twMerge(
        'link-editor flex items-center absolute top-0 left-0 px-16 w-full h-[42px] bg-grayscale0 z-10',
        lastSelection !== null ? 'visible' : 'invisible',
      )}
    >
      {isEditMode ? (
        <div className="link-input flex items-center gap-x-16">
          <Input
            fieldSize="small"
            ref={inputRef}
            className="link-input"
            value={linkUrl}
            onChange={(event) => {
              setLinkUrl(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                if (lastSelection !== null) {
                  if (linkUrl !== '') {
                    editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl);
                  }
                  setEditMode(false);
                }
              } else if (event.key === 'Escape') {
                event.preventDefault();
                setEditMode(false);
              }
            }}
          />
          <ActionButton
            size="large"
            icon={IconCheck}
            onPress={() => {
              if (linkUrl !== '') {
                editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl);
              }
              return setEditMode(false);
            }}
            className="size-[40px]"
          />
        </div>
      ) : (
        <div className="link-input  flex items-center gap-x-16">
          <Typography as="p">{linkUrl}</Typography>
          <ActionButton icon={IconPen} onPress={() => setEditMode(true)} />
        </div>
      )}
    </div>
  );
}

export default function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);

  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isUnorderedList, setIsUnorderedList] = useState(false);
  const [isOrderedList, setIsOrderedList] = useState(false);
  const [isLink, setIsLink] = useState(false);

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://');
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  const $updateToolbar = useCallback(() => {
    // const dwadaw = $getListDepth();
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));

      let isUnorderedList = false;
      let isOrderedList = false;

      // Check if the selection is in an unordered or ordered list
      const anchorNode = selection.anchor.getNode();
      const focusNode = selection.focus.getNode();
      const anchorParent = anchorNode.getParent();
      const focusParent = focusNode.getParent();

      if ($isListNode(anchorParent) && $isListNode(focusParent)) {
        const anchorType = anchorParent.getType();
        const focusType = focusParent.getType();

        // Check if both anchor and focus are in the same type of list
        if (anchorType === focusType) {
          isUnorderedList = anchorType === 'bullet';
          isOrderedList = anchorType === 'number';
        }
      }

      setIsUnorderedList(isUnorderedList);
      setIsOrderedList(isOrderedList);

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();

      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }
    }
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          $updateToolbar();
          return false;
        },
        LowPriority,
      ),
      editor.registerCommand(
        INSERT_UNORDERED_LIST_COMMAND,
        () => {
          insertList(editor, 'bullet');
          return true;
        },
        LowPriority,
      ),
      editor.registerCommand(
        INSERT_ORDERED_LIST_COMMAND,
        () => {
          insertList(editor, 'number');
          return true;
        },
        LowPriority,
      ),
    );
  }, [editor, $updateToolbar]);

  return (
    <div
      className="flex border-b border-grayscale100 py-8 px-12"
      ref={toolbarRef}
    >
      <Divider />
      <div className="flex flex-row items-center gap-x-24">
        <div className="flex flex-row items-center gap-x-8">
          <RichTextEditorAction
            icon={IconBold}
            onPress={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
            state={isBold ? 'active' : 'inactive'}
          />
          <RichTextEditorAction
            icon={IconItalic}
            onPress={() =>
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')
            }
            state={isItalic ? 'active' : 'inactive'}
          />
          <RichTextEditorAction
            icon={IconUnderline}
            onPress={() =>
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')
            }
            state={isUnderline ? 'active' : 'inactive'}
          />
          <RichTextEditorAction
            icon={IconTextStrikeThrough}
            onPress={() =>
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')
            }
            state={isStrikethrough ? 'active' : 'inactive'}
          />
        </div>

        <div className="flex flex-row items-center gap-x-8">
          <RichTextEditorAction
            icon={IconListOl}
            onPress={() =>
              editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
            }
            state={isOrderedList ? 'active' : 'inactive'}
          />
          <RichTextEditorAction
            icon={IconListUl}
            onPress={() =>
              editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
            }
            state={isUnorderedList ? 'active' : 'inactive'}
          />
        </div>
        <div className="flex flex-row items-center gap-x-8">
          <RichTextEditorAction
            icon={IconLinkAlt}
            onPress={insertLink}
            state={isLink ? 'active' : 'inactive'}
          />
          {isLink && <FloatingLinkEditor editor={editor} />}
        </div>
      </div>
    </div>
  );
}
